import { FC, useCallback, useMemo } from "react";
import { MotivationGroupEvaluated } from "tm-shared/types";
import { formatNumber } from "tm-shared/utils";

import BIcon from "@/assets/icons/kpi-b.svg";
import LIcon from "@/assets/icons/kpi-l.svg";
import MIcon from "@/assets/icons/kpi-m.svg";
import PIcon from "@/assets/icons/kpi-p.svg";
import FlexContainer from "@/components/FlexContainer";
import { PrimaryText, SecondaryText } from "@/components/Typography/Styled";
import {
    DashboardCollapse,
    DashboardCollapseItemType,
    DashboardCollapseListItem,
} from "@/pages/Dashboard/components/DashboardCollapse";
import { DashboardProgress } from "@/pages/Dashboard/components/DashboardProgress";

import { StyledFactValue, StyledProductTagSpecial } from "./Styled";

const ICONS = { b: BIcon, l: LIcon, m: MIcon, p: PIcon } as const;
const MEASUREMENTS = { b: "пл.", l: "л.", m: "грн.", p: "ТТ" } as const;

interface KPICollapseProps {
    evaluatedMotivationGroup: MotivationGroupEvaluated;
    setModalData: (data: {
        title: string;
        type: DashboardCollapseItemType;
        details:
            | { finGroups?: string[] }
            | {
                  currentPercent?: number;
                  progressive?: { from: number; coefficient: number }[];
              }
            | { minComplete?: number; maxComplete?: number };
    }) => void;
}

export const KPICollapse: FC<KPICollapseProps> = ({
    evaluatedMotivationGroup,
    setModalData,
}) => {
    const {
        groupType,
        name,
        compensation: { maxComplete },
        motivationType,
        finGroups,
        salaryPercent,
        planGrowthCoefficient,
        context,
    } = evaluatedMotivationGroup.group;

    const {
        statistic: { fact: factCount, plan: planCount, percent },
    } = evaluatedMotivationGroup.evaluation;

    const bonus = evaluatedMotivationGroup.evaluation.bonus;

    const compensation = evaluatedMotivationGroup.group.compensation;

    const progressive =
        "scales" in compensation ? compensation.scales : undefined;

    const minComplete =
        "minComplete" in compensation ? compensation.minComplete : undefined;

    const isDropout = motivationType === "s";
    const isProgressive = progressive?.length > 0;
    const maxPercent = isProgressive ? progressive[0].from : minComplete;
    const iconSrc = ICONS[motivationType] || "";

    const scaleItems = useMemo(() => {
        return isProgressive
            ? [
                  {
                      label: "Перший крок:",
                      value: `${progressive[0].from}% - ${progressive[0].coefficient}`,
                      type: "basic",
                  },
                  {
                      label: "Останній крок:",
                      value: `${progressive.at(-1)?.from}% - ${progressive.at(
                          -1,
                      )?.coefficient}`,
                      type: "basic",
                  },
              ]
            : [
                  {
                      label: "Зараховується від:",
                      value: `${minComplete ?? 0}%`,
                      type: "basic",
                  },
                  {
                      label: "Зараховується до:",
                      value: `${maxComplete ?? 0}%`,
                      type: "basic",
                  },
              ];
    }, [isProgressive, progressive, minComplete, maxComplete]);

    const finGroupsItem = useMemo(() => {
        if (groupType === "common") return undefined;
        const isSingle = finGroups.length === 1;
        return {
            label: `Груп${isSingle ? "а" : "и"} фін. обліку:`,
            value: isSingle
                ? finGroups[0]
                : `${finGroups[0]} + ${finGroups.length - 1}`,
            type: isSingle ? "basic" : "fin_groups",
        } as const;
    }, [groupType, finGroups]);

    const collapseItems = useMemo(() => {
        const baseItems = finGroupsItem ? [finGroupsItem] : [];

        if (isDropout) {
            return [
                ...baseItems,
                {
                    label: "Сума продажу:",
                    value: `${factCount} грн.`,
                    type: "basic",
                },
                {
                    label: "Відсоток бонусу:",
                    value: `${salaryPercent}%`,
                    type: "basic",
                },
                {
                    label: "Бонус:",
                    value: `${bonus.current} грн.`,
                    type: "basic",
                },
            ];
        }

        return [
            ...baseItems,
            {
                label: "План:",
                value: `${planCount} ${MEASUREMENTS[motivationType]}`,
                type: "basic",
            },
            {
                label: "Факт:",
                value: `${factCount} ${MEASUREMENTS[motivationType]}`,
                type: "basic",
            },
            ...(motivationType === "p"
                ? [
                      //   {
                      //       label: "Відсоток від АКБ:",
                      //       value: context.akbPercent,
                      //       type: "basic",
                      //   },
                      //   {
                      //       label: "Період АКБ:",
                      //       value: `${dayjs(targetDate)
                      //           .startOf("month")
                      //           .subtract(context.monthsPeriod, "month")
                      //           .format("DD.MM.YYYY")} - ${dayjs(targetDate)
                      //           .startOf("month")
                      //           .subtract(1, "day")
                      //           .format("DD.MM.YYYY")}`,
                      //       type: "basic",
                      //   },
                      {
                          label: "Мін. кількість товарів:",
                          value: `${context.minCountProducts} шт.`,
                          type: "basic",
                      },
                      {
                          label: "Унікальних СКЮ:",
                          value: `${context.minCountSKU} шт.`,
                          type: "basic",
                      },
                  ]
                : []),
            ...(groupType === "special"
                ? [
                      //   {
                      //       label: "Відсоток від АКБ:",
                      //       value: context.akbPercent,
                      //       type: "basic",
                      //   },
                      //   {
                      //       label: "Період АКБ:",
                      //       value: `${dayjs(targetDate)
                      //           .startOf("month")
                      //           .subtract(context.monthsPeriod, "month")
                      //           .format("DD.MM.YYYY")} - ${dayjs(targetDate)
                      //           .startOf("month")
                      //           .subtract(1, "day")
                      //           .format("DD.MM.YYYY")}`,
                      //       type: "basic",
                      //   },
                      {
                          label: "Коефіцієнт збільшення плану:",
                          value: planGrowthCoefficient,
                          type: "basic",
                      },
                  ]
                : []),
            {
                label: "Виконання плану:",
                value: `${formatNumber(percent, 2)}%`,
                type: "basic",
            },
            {
                label: "Тип розрахунку:",
                value: isProgressive ? "Прогресивний" : "Послідовний",
                type: isProgressive ? "progressive_scale" : "sequential_scale",
            },
            ...scaleItems,
            {
                label: "Бонус:",
                value: `${bonus.current} / ${bonus.max ?? 0} грн.`,
                type: "basic",
            },
        ];
    }, [
        finGroupsItem,
        isDropout,
        motivationType,
        factCount,
        salaryPercent,
        bonus,
        planCount,
        percent,
        isProgressive,
        scaleItems,
    ]);

    const handleInfoClick = useCallback(
        (type: DashboardCollapseItemType) => {
            const details =
                type === "fin_groups"
                    ? { finGroups }
                    : type === "progressive_scale"
                      ? { currentPercent: percent, progressive }
                      : { minComplete, maxComplete };

            setModalData({ title: name, type, details });
        },
        [
            finGroups,
            percent,
            progressive,
            minComplete,
            maxComplete,
            name,
            setModalData,
        ],
    );

    return (
        <DashboardCollapse
            header={
                <FlexContainer
                    width="100%"
                    flexDirection="column"
                    gap="2px"
                    alignItems="flex-start"
                >
                    <FlexContainer
                        width="100%"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        gap="8px"
                    >
                        <FlexContainer
                            gap="8px"
                            alignItems="center"
                            flexWrap="wrap"
                        >
                            {groupType === "special" && (
                                <StyledProductTagSpecial isDropout={isDropout}>
                                    {isDropout ? "Вибуття" : "Спецзадача"}
                                </StyledProductTagSpecial>
                            )}
                            <SecondaryText fontWeight="400" fontSize={14}>
                                {name}
                            </SecondaryText>
                        </FlexContainer>
                        <PrimaryText fontWeight="500" fontSize={14} $textNoWrap>
                            {bonus.current} грн.
                        </PrimaryText>
                    </FlexContainer>

                    {!isDropout && (
                        <FlexContainer
                            gap="16px"
                            alignItems="center"
                            width="100%"
                        >
                            <DashboardProgress
                                value={Math.round(percent)}
                                limit={maxPercent}
                            />
                            <FlexContainer gap="4px" alignItems="center">
                                <FlexContainer
                                    flexDirection="column"
                                    alignItems="flex-end"
                                    width="100%"
                                >
                                    <StyledFactValue
                                        fontWeight="500"
                                        fontSize={14}
                                        lineHeight="120%"
                                        style={{ whiteSpace: "nowrap" }}
                                        isFactComleted={percent >= maxPercent}
                                    >
                                        {factCount}
                                    </StyledFactValue>
                                    <PrimaryText
                                        fontWeight="500"
                                        fontSize={10}
                                        lineHeight="100%"
                                        style={{ whiteSpace: "nowrap" }}
                                    >
                                        {planCount}
                                    </PrimaryText>
                                </FlexContainer>
                                {iconSrc && (
                                    <img
                                        src={iconSrc}
                                        alt={`${motivationType}-icon`}
                                    />
                                )}
                            </FlexContainer>
                        </FlexContainer>
                    )}
                </FlexContainer>
            }
            items={collapseItems as DashboardCollapseListItem[]}
            onInfoClick={handleInfoClick}
        />
    );
};
